import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaComments,
  FaDownload,
  FaInfoCircle,
  FaPaperPlane,
  FaPercent,
  FaRupeeSign,
} from "react-icons/fa";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  fetchMessageAnalytics,
  fetchPricingAnalytics,
} from "../api/wabaAnalytics.api";
import { SAMPLE_PROJECT_ID } from "../api/staticInteractedCustomer";
import { useProjectPhone } from "../hooks/useProjectPhone";
import { useTheme } from "../theme/ThemeContext";

const THEME_DELIVERED = "#16a34a";

const BASE_CHART_COLORS = [
  THEME_DELIVERED,
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
];

const toIstDate = (unix) =>
  new Date(unix * 1000).toLocaleDateString("en-CA", {
    timeZone: "Asia/Kolkata",
  });

const formatDateInput = (date) =>
  date.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const formatDisplayDate = (iso) => {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00+05:30`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
};

const formatCount = (value) => Number(value || 0).toLocaleString("en-IN");

const istStartUnix = (dateStr) =>
  Math.floor(new Date(`${dateStr}T00:00:00+05:30`).getTime() / 1000);

const istEndUnix = (dateStr) =>
  Math.floor(new Date(`${dateStr}T23:59:59+05:30`).getTime() / 1000);

const defaultDateRange = () => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 10);
  return { from: formatDateInput(from), to: formatDateInput(to) };
};

const processMessagePoints = (dataPoints = []) =>
  dataPoints
    .map((point) => ({
      ist_date: toIstDate(point.start),
      start: point.start,
      sent: Number(point.sent || 0),
      delivered: Number(point.delivered || 0),
    }))
    .sort((a, b) => a.start - b.start);

const CONVERSATION_SERIES = "Conversations";

const isConversationPoint = (point) =>
  point?.conversation != null &&
  point?.pricing_category == null &&
  point?.volume == null;

const extractPricingDataPoints = (response) => {
  const body = response?.data?.data ?? response?.data ?? response;
  const analyticsBlock =
    body?.pricing_analytics ?? body?.conversation_analytics;
  if (!Array.isArray(analyticsBlock?.data)) return null;
  const points = analyticsBlock.data.flatMap((entry) => entry?.data_points ?? []);
  return points.length ? points : null;
};

const finalizePricingRows = (byDay, series) => {
  const rows = Object.values(byDay)
    .map((row) => {
      series.forEach((key) => {
        if (!(key in row)) row[key] = 0;
        const costKey = `__cost_${key}`;
        if (!(costKey in row)) row[costKey] = 0;
      });
      return row;
    })
    .sort((a, b) => (a.ist_date > b.ist_date ? 1 : -1));
  return { rows, series };
};

const processConversationPoints = (dataPoints = []) => {
  const byDay = {};

  dataPoints.forEach((point) => {
    const istDate = toIstDate(point.start);
    if (!byDay[istDate]) {
      byDay[istDate] = { ist_date: istDate, __total_cost: 0, __total_volume: 0 };
    }
    const volume = Number(point.conversation || 0);
    const cost = Number(point.cost || 0);
    byDay[istDate][CONVERSATION_SERIES] =
      (byDay[istDate][CONVERSATION_SERIES] || 0) + volume;
    byDay[istDate][`__cost_${CONVERSATION_SERIES}`] =
      (byDay[istDate][`__cost_${CONVERSATION_SERIES}`] || 0) + cost;
    byDay[istDate].__total_cost += cost;
    byDay[istDate].__total_volume += volume;
  });

  return finalizePricingRows(byDay, [CONVERSATION_SERIES]);
};

const processCategoryPricingPoints = (dataPoints = []) => {
  const seriesSet = new Set();
  const byDay = {};

  dataPoints.forEach((point) => {
    const istDate = toIstDate(point.start);
    const seriesKey = `${point.pricing_category || "UNKNOWN_CAT"} | ${point.pricing_type || "UNKNOWN_TYPE"} | ${point.country || "UNKNOWN_COUNTRY"}`;
    seriesSet.add(seriesKey);
    if (!byDay[istDate]) {
      byDay[istDate] = { ist_date: istDate, __total_cost: 0, __total_volume: 0 };
    }
    byDay[istDate][seriesKey] =
      (byDay[istDate][seriesKey] || 0) + Number(point.volume || 0);
    const costKey = `__cost_${seriesKey}`;
    byDay[istDate][costKey] =
      (byDay[istDate][costKey] || 0) + Number(point.cost || 0);
    byDay[istDate].__total_cost += Number(point.cost || 0);
    byDay[istDate].__total_volume += Number(point.volume || 0);
  });

  return finalizePricingRows(byDay, Array.from(seriesSet));
};

const processPricingPoints = (dataPoints = []) => {
  if (!dataPoints.length) return { rows: [], series: [] };
  if (dataPoints.some(isConversationPoint)) {
    return processConversationPoints(dataPoints);
  }
  return processCategoryPricingPoints(dataPoints);
};

const downloadCsv = (filename, headers, rows) => {
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const lines = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const MessageTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const sent = payload.find((p) => p.dataKey === "sent")?.value ?? 0;
  const delivered = payload.find((p) => p.dataKey === "delivered")?.value ?? 0;
  return (
    <div className="waba-tooltip">
      <div className="waba-tooltip-title">{label}</div>
      <div className="waba-tooltip-row">
        <span>Delivered</span>
        <strong>{delivered}</strong>
      </div>
      <div className="waba-tooltip-row">
        <span>Sent</span>
        <strong>{sent}</strong>
      </div>
    </div>
  );
};

const PricingTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((sum, p) => sum + Number(p.value || 0), 0);
  return (
    <div className="waba-tooltip">
      <div className="waba-tooltip-title">{label}</div>
      <div className="waba-tooltip-row">
        <span>Total</span>
        <strong>{total}</strong>
      </div>
      <div className="waba-tooltip-subtitle">Volume by series</div>
      <div className="waba-tooltip-list">
        {payload
          .filter((p) => p.value)
          .slice(0, 8)
          .map((p) => (
            <div key={p.dataKey} className="waba-tooltip-item">
              <span className="waba-tooltip-swatch" style={{ background: p.color }} />
              <span className="waba-tooltip-key">{p.name}</span>
              <strong className="waba-tooltip-val">{p.value}</strong>
            </div>
          ))}
      </div>
    </div>
  );
};

const InfoTip = ({ label, text }) => (
  <span className="waba-info-tip">
    <button
      type="button"
      className="waba-info-btn"
      aria-label={label}
      title={label}
    >
      <FaInfoCircle />
    </button>
    <span className="waba-info-popover" role="tooltip">
      {text}
    </span>
  </span>
);

const Analytics = () => {
  const { theme } = useTheme();
  const themePrimary = theme.primary;
  const chartColors = [themePrimary, ...BASE_CHART_COLORS];
  const { projectId: routeProjectId, projectLoading } = useProjectPhone();
  const projectId = routeProjectId || SAMPLE_PROJECT_ID;

  const [dateFrom, setDateFrom] = useState(() => defaultDateRange().from);
  const [dateTo, setDateTo] = useState(() => defaultDateRange().to);
  const [messageRows, setMessageRows] = useState([]);
  const [pricingRows, setPricingRows] = useState([]);
  const [pricingSeries, setPricingSeries] = useState([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [messageError, setMessageError] = useState("");
  const [pricingError, setPricingError] = useState("");
  const [useInr, setUseInr] = useState(true);

  const loadAnalytics = useCallback(async () => {
    if (!projectId) return;
    const startsunix = istStartUnix(dateFrom);
    const endunix = istEndUnix(dateTo);

    setMessageLoading(true);
    setPricingLoading(true);
    setMessageError("");
    setPricingError("");

    try {
      const [msgRes, priceRes] = await Promise.all([
        fetchMessageAnalytics(projectId, startsunix, endunix),
        fetchPricingAnalytics(projectId, startsunix, endunix),
      ]);

      const msgBody = msgRes?.data ?? msgRes;
      const msgInner = msgBody?.data ?? msgBody;
      const analytics = msgInner?.analytics ?? msgBody?.analytics;
      const points = analytics?.data_points;

      if (!Array.isArray(points)) {
        setMessageRows([]);
        setMessageError("No analytics data available");
      } else {
        setMessageRows(processMessagePoints(points));
      }

      const pricePoints = extractPricingDataPoints(priceRes);

      if (!pricePoints) {
        setPricingRows([]);
        setPricingSeries([]);
        setPricingError("No pricing data available");
      } else {
        const { rows, series } = processPricingPoints(pricePoints);
        setPricingRows(rows);
        setPricingSeries(series);
      }
    } catch (err) {
      setMessageError(err.message || "Failed to fetch analytics");
      setPricingError(err.message || "Failed to fetch pricing analytics");
      setMessageRows([]);
      setPricingRows([]);
      setPricingSeries([]);
    } finally {
      setMessageLoading(false);
      setPricingLoading(false);
    }
  }, [projectId, dateFrom, dateTo]);

  useEffect(() => {
    if (projectLoading) return;
    loadAnalytics();
  }, [loadAnalytics, projectLoading]);

  const messageTotals = useMemo(
    () =>
      messageRows.reduce(
        (acc, row) => ({
          sent: acc.sent + row.sent,
          delivered: acc.delivered + row.delivered,
        }),
        { sent: 0, delivered: 0 },
      ),
    [messageRows],
  );

  const pricingTotals = useMemo(
    () =>
      pricingRows.reduce(
        (acc, row) => ({
          volume: acc.volume + Number(row.__total_volume || 0),
          cost: acc.cost + Number(row.__total_cost || 0),
        }),
        { volume: 0, cost: 0 },
      ),
    [pricingRows],
  );

  const isConversationPricing =
    pricingSeries.length === 1 && pricingSeries[0] === CONVERSATION_SERIES;

  const deliveryRate = messageTotals.sent
    ? (messageTotals.delivered / messageTotals.sent) * 100
    : 0;

  const periodLabel = `${formatDisplayDate(dateFrom)} – ${formatDisplayDate(dateTo)}`;

  const formatXAxis = useCallback((value) => String(value || "").slice(5), []);

  const exportMessageCsv = () => {
    downloadCsv(
      "message-analytics.csv",
      ["date", "sent", "delivered"],
      messageRows.map((row) => ({
        date: row.ist_date,
        sent: row.sent,
        delivered: row.delivered,
      })),
    );
  };

  const exportPricingCsv = () => {
    const headers = ["date", ...pricingSeries, "total_volume", "total_cost"];
    downloadCsv(
      "pricing-analytics.csv",
      headers,
      pricingRows.map((row) => {
        const out = { date: row.ist_date };
        pricingSeries.forEach((key) => {
          out[key] = row[key] || 0;
        });
        out.total_volume = row.__total_volume || 0;
        out.total_cost = row.__total_cost || 0;
        return out;
      }),
    );
  };

  return (
    <div className="waba-analytics-page">
      <header className="waba-analytics-header">
        <div>
          <h1>
            <FaChartLine />
            Analytics
          </h1>
          <p>
            WhatsApp Business Account message delivery and pricing insights for{" "}
            {periodLabel}. All dates use Asia/Kolkata (IST).
          </p>
        </div>
        <div className="waba-analytics-toolbar">
          <label>
            <span>
              <FaCalendarAlt /> From
            </span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label>
            <span>
              <FaCalendarAlt /> To
            </span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          <button type="button" className="biz-btn-primary" onClick={loadAnalytics}>
            Apply
          </button>
          <button
            type="button"
            className="biz-btn-secondary"
            onClick={exportMessageCsv}
            disabled={!messageRows.length}
          >
            <FaDownload />
            Messages
          </button>
          <button
            type="button"
            className="biz-btn-secondary"
            onClick={exportPricingCsv}
            disabled={!pricingRows.length}
          >
            <FaDownload />
            Pricing
          </button>
        </div>
      </header>

      <section className="waba-kpi-grid" aria-label="Analytics totals">
        <article className="waba-kpi-card waba-kpi-sent">
          <span className="waba-kpi-icon">
            <FaPaperPlane />
          </span>
          <div>
            <span className="waba-kpi-label">
              Total sent
              <InfoTip
                label="Info about total sent"
                text="The total number of messages your business sent to individual accounts. This metric counts all messages sent, including messages that may not have been successfully delivered."
              />
            </span>
            <strong>{formatCount(messageTotals.sent)}</strong>
          </div>
        </article>
        <article className="waba-kpi-card waba-kpi-delivered">
          <span className="waba-kpi-icon">
            <FaCheckCircle />
          </span>
          <div>
            <span className="waba-kpi-label">
              Total delivered
              <InfoTip
                label="Info about total delivered"
                text="The number of messages that your business sent that were successfully delivered to the recipient's device."
              />
            </span>
            <strong>{formatCount(messageTotals.delivered)}</strong>
          </div>
        </article>
        <article className="waba-kpi-card waba-kpi-rate">
          <span className="waba-kpi-icon">
            <FaPercent />
          </span>
          <div>
            <span className="waba-kpi-label">Delivery rate</span>
            <strong>{deliveryRate.toFixed(1)}%</strong>
          </div>
        </article>
        <article className="waba-kpi-card waba-kpi-volume">
          <span className="waba-kpi-icon">
            <FaComments />
          </span>
          <div>
            <span className="waba-kpi-label">
              {isConversationPricing ? "Conversations" : "Billable volume"}
              <InfoTip
                label={
                  isConversationPricing
                    ? "Info about total conversations"
                    : "Info about total volume"
                }
                text={
                  isConversationPricing
                    ? "Total number of billable conversations for the selected date range."
                    : "Total Volume is the count of all delivered messages for the selected date range that will be charged by Meta."
                }
              />
            </span>
            <strong>{formatCount(pricingTotals.volume)}</strong>
          </div>
        </article>
        <article className="waba-kpi-card waba-kpi-cost">
          <span className="waba-kpi-icon">
            <FaRupeeSign />
          </span>
          <div>
            <span className="waba-kpi-label">
              Total cost
              <InfoTip
                label="Info about total cost"
                text="Total charges for messages delivered on WhatsApp. Charges depend on country and message category."
              />
            </span>
            <strong>
              {useInr ? "₹" : "$"}
              {Number(pricingTotals.cost || 0).toFixed(2)}
            </strong>
          </div>
        </article>
      </section>

      <section className="waba-analytics-grid waba-analytics-grid-top">
        <article className="waba-panel waba-panel-wide">
          <div className="waba-panel-head">
            <div>
              <h2>Message performance</h2>
              <p>Sent volume as bars, delivered as a line — one point per IST day.</p>
            </div>
          </div>
          <div className="waba-chart-area">
            {messageLoading ? (
              <p className="waba-chart-empty">Loading message analytics…</p>
            ) : messageRows.length === 0 ? (
              <p className="waba-chart-empty">
                {messageError || "No message data for this date range."}
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={messageRows} margin={{ top: 8, right: 16, left: 0, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="ist_date"
                    tickFormatter={formatXAxis}
                    angle={-45}
                    textAnchor="end"
                    height={48}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                  <Tooltip content={<MessageTooltip />} />
                  <Legend />
                  <Bar dataKey="sent" name="Sent" barSize={16} fill={themePrimary} radius={[4, 4, 0, 0]} />
                  <Line
                    type="monotone"
                    dataKey="delivered"
                    name="Delivered"
                    stroke={THEME_DELIVERED}
                    strokeWidth={2}
                    dot={{ r: 3, fill: THEME_DELIVERED }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
          {messageError && messageRows.length > 0 && (
            <p className="waba-panel-error">{messageError}</p>
          )}
        </article>

        <article className="waba-panel">
          <div className="waba-panel-head">
            <div>
              <h2>Delivery summary</h2>
              <p>Totals for the selected range.</p>
            </div>
          </div>
          <div className="waba-totals">
            <div className="waba-total-row">
              <span>Total sent</span>
              <strong>{formatCount(messageTotals.sent)}</strong>
            </div>
            <div className="waba-total-row">
              <span>Total delivered</span>
              <strong>{formatCount(messageTotals.delivered)}</strong>
            </div>
            <div className="waba-total-row">
              <span>Delivery rate</span>
              <strong>{deliveryRate.toFixed(1)}%</strong>
            </div>
            <p className="waba-tz-note">Data timezone: Asia/Kolkata (IST)</p>
          </div>
        </article>
      </section>

      <section className="waba-analytics-grid">
        <article className="waba-panel waba-panel-wide">
          <div className="waba-panel-head">
            <div>
              <h2>
                {isConversationPricing
                  ? "Conversation volume"
                  : "Pricing volume"}
              </h2>
              <p>
                {isConversationPricing
                  ? "Billable conversations counted per IST day."
                  : "Stacked by category, type, and country for each IST day."}
              </p>
            </div>
          </div>
          <div className="waba-chart-area waba-chart-area-tall">
            {pricingLoading ? (
              <p className="waba-chart-empty">Loading pricing analytics…</p>
            ) : pricingRows.length === 0 ? (
              <p className="waba-chart-empty">
                {pricingError || "No pricing data for this date range."}
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pricingRows} margin={{ top: 8, right: 16, left: 0, bottom: 64 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="ist_date"
                    tickFormatter={formatXAxis}
                    angle={-45}
                    textAnchor="end"
                    height={64}
                    tick={{ fill: "#64748b", fontSize: 12 }}
                  />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                  <Tooltip content={<PricingTooltip />} wrapperStyle={{ zIndex: 99999 }} />
                  <Legend wrapperStyle={{ bottom: -10 }} />
                  {pricingSeries.map((key, idx) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={key}
                      stackId="a"
                      barSize={18}
                      fill={chartColors[idx % chartColors.length]}
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          {pricingError && pricingRows.length > 0 && (
            <p className="waba-panel-error">{pricingError}</p>
          )}
        </article>

        <article className="waba-panel">
          <div className="waba-panel-head">
            <div>
              <h2>Pricing summary</h2>
              <p>
                {isConversationPricing
                  ? "Conversation count per day"
                  : "Category · type · country"}
              </p>
            </div>
          </div>
          <div className="waba-totals">
            <div className="waba-total-row">
              <span>
                {isConversationPricing ? "Total conversations" : "Total volume"}
              </span>
              <strong>{formatCount(pricingTotals.volume)}</strong>
            </div>
            <div className="waba-total-row">
              <span>Total cost</span>
              <strong>
                {useInr ? "₹" : "$"}
                {Number(pricingTotals.cost || 0).toFixed(2)}
              </strong>
            </div>
            <div className="waba-series-list">
              <strong>Series shown</strong>
              <ul>
                {pricingSeries.length === 0 && (
                  <li className="waba-series-empty">No series available</li>
                )}
                {pricingSeries.map((key, index) => (
                  <li key={key}>
                    <span
                      className="waba-series-swatch"
                      style={{
                        background: chartColors[index % chartColors.length],
                      }}
                    />
                    {key}
                  </li>
                ))}
              </ul>
            </div>
            <p className="waba-tz-note">
              Pricing is aggregated per IST day. Volumes are message counts.
            </p>
            <label className="waba-currency-toggle">
              <input
                type="checkbox"
                checked={useInr}
                onChange={(e) => setUseInr(e.target.checked)}
              />
              Show cost in INR (₹)
            </label>
          </div>
        </article>
      </section>
    </div>
  );
};

export default Analytics;
